import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { SimpleChatMessageBubble } from "./SimpleChatMessageBubble";
import { SimpleChatMessageFooter } from "./SimpleChatMessageFooter";
import { SimpleChatMessageDirectionProvider, type SimpleChatMessageDirection } from "./useSimpleChatMessageDirection";

/**
 * Deliberately the SIMPLE case: plain-text bubbles, an optional footer, nothing else. It is named
 * for that limit so the name `ChatMessage` stays free for the full inbox message, and so anyone
 * reaching for this to back the inbox sees the gap before wiring it in rather than after. The
 * inbox additionally needs image/audio/file attachments, a sender avatar beside the bubble, sender
 * identity and timestamps, delivery and read state, and grouping of consecutive messages from one
 * sender -- none of which this component does.
 *
 * The compound shape keeps that gap cheap to close. The root renders only layout and owns
 * `direction` in context; everything visible is `children`, so a new part reads direction from
 * context and slots into the existing column instead of the root growing a prop per variation. The
 * layout is a ROW -- reversed when outbound -- whose single child is the message column, which
 * leaves the row's other slot free without touching the column, the alignment, or any consumer.
 *
 * Deliberately not AI-chat-specific: the same two-party shape shows up wherever the product has
 * messaging (an AI assistant, a care-team/patient conversation, internal team chat). Anything
 * AI-flavored -- rating a response, say -- is composed into `.Footer` by the consumer rather than
 * baked in, which is why the feedback row lives in the shared `AIFeedbackButtons`.
 *
 * It sits in `molecules/` even though, with the feedback row extracted, its own imports would today
 * read as an atom under the strict classification rule -- the attachment and avatar parts it is
 * shaped to accept compose `Avatar` and `FilePreview`, so classifying by where it is going avoids a
 * move-and-move-back.
 *
 * @example
 * ```tsx
 * <SimpleChatMessage direction="inbound">
 *   <SimpleChatMessage.Bubble>{message.content}</SimpleChatMessage.Bubble>
 *   <SimpleChatMessage.Footer>
 *     <AIFeedbackButtons onFeedback={...} value={feedback} />
 *   </SimpleChatMessage.Footer>
 * </SimpleChatMessage>
 * ```
 */
export interface SimpleChatMessageProps {
  /**
   * Which way the message travelled -- a property of the message, not of who is viewing it.
   * `outbound` is right-aligned and primary-colored, `inbound` left-aligned and muted.
   */
  direction: SimpleChatMessageDirection;
  className?: string;
  /** The message's parts: one or more `.Bubble`s, and optionally a `.Footer`. */
  children?: ReactNode;
}

const SimpleChatMessageRoot = ({ direction, className, children }: SimpleChatMessageProps) => (
  <SimpleChatMessageDirectionProvider value={direction}>
    <div
      data-slot="simple-chat-message"
      data-direction={direction}
      className={cn("flex", direction === "outbound" && "flex-row-reverse", className)}
    >
      <div
        className={cn(
          // The measure sits on the column, not the bubble, so stacked bubbles and the footer
          // share one width rather than each computing its own.
          "flex max-w-[85%] flex-col gap-1",
          direction === "outbound" ? "items-end" : "items-start"
        )}
      >
        {children}
      </div>
    </div>
  </SimpleChatMessageDirectionProvider>
);

export const SimpleChatMessage = Object.assign(SimpleChatMessageRoot, {
  Bubble: SimpleChatMessageBubble,
  Footer: SimpleChatMessageFooter,
});
